import { navigate } from '../actions';
import { doCheckImportVisit } from '../combo';
import {
    CHILD_CLINIC_HISTORY_KEY,
    CHILD_HISTORY_KEY,
    ONLINE_BOOKING_CHILD_CLINIC,
    ONLINE_BOOKING_CLINIC,
    STORE_PROFILE_CHILD,
    USER_AGREEMENT_KEY,
    USER_CLINIC_HISTORY_KEY,
    USER_HISTORY_KEY
} from '../hooks/usePersonalData';
import {
    filterConsentsByCards,
    makeConsentObject
} from '../selectors/consents';
import { eventBus } from '../services/eventBus';
import store from '../store';

// Default clinic history keys
export const CLINIC_CONSENT_KEYS = {
    USER: USER_CLINIC_HISTORY_KEY,
    CHILD: CHILD_CLINIC_HISTORY_KEY,
};

export const shouldShowConsentCheckbox = (consentValue) => {
    if (consentValue === true || consentValue === false) {
        return false;
    }
    return true;
};

/**
 * Gets the list of providers (clinics) for displaying clinic consent checkboxes
 * @param {Object} consents - consents object
 * @param {string} clinicHistoryKey - consent key for clinic history (USER_CLINIC_HISTORY_KEY or CHILD_CLINIC_HISTORY_KEY)
 * @param {boolean} alwaysShow - if true, show consent regardless of value
 * @param {Object} providerCapabilities - optional object with provider capabilities from getProviderCapabilities
 * @returns {Array} array of objects with provider information: {providerCode, consentValue, shouldShow}
 */
export const getClinicsConsents = (consents, clinicHistoryKey = USER_CLINIC_HISTORY_KEY, alwaysShow = false, providerCapabilities = null) => {
    if (!consents) {
        return [];
    }

    const clinicsConsents = [];

    // Find which capability is needed for this consent key
    const getRequiredCapability = () => {
        if (!providerCapabilities) {
            return null;
        }

        // Map consent keys to capability names
        if (clinicHistoryKey === USER_CLINIC_HISTORY_KEY || clinicHistoryKey === CHILD_CLINIC_HISTORY_KEY) {
            return 'MedicalHistoryConsentGathering';
        }
        if (clinicHistoryKey === ONLINE_BOOKING_CLINIC || clinicHistoryKey === ONLINE_BOOKING_CHILD_CLINIC) {
            return 'OnlineBookingConsentGathering';
        }
        return null;
    };

    const requiredCapability = getRequiredCapability();

    // `consents` is per-profile: { main: {...}, [providerCode]: { MEDICAL_DATA_HISTORY_CLINIC?, ONLINE_BOOKING_CLINIC?, ... }, ... }
    // Here `key` is providerCode; `consents[key]` is that clinic's consent fields (not the code string).
    Object.keys(consents).forEach((key) => {
        if (key !== 'main') {
            const clinicConsents = consents[key];
            const consentValue = clinicConsents?.[clinicHistoryKey];
            let shouldShow = alwaysShow ? true : shouldShowConsentCheckbox(consentValue);
            // If providerCapabilities is provided, check if provider has required capability
            if (providerCapabilities && requiredCapability) {
                const providerCaps = providerCapabilities[key];
                if (!providerCaps || !Array.isArray(providerCaps) || providerCaps.length === 0) {
                    // No metadata (e.g. no card yet): still show row if consent already granted
                    if (consentValue !== true) {
                        shouldShow = false;
                    }
                } else if (alwaysShow) {
                    const hasConsentKeyInStore = clinicConsents != null
                        && Object.prototype.hasOwnProperty.call(clinicConsents, clinicHistoryKey);
                    shouldShow = (providerCaps.includes(requiredCapability) || consentValue === true)
                        && hasConsentKeyInStore;
                }
            }

            clinicsConsents.push({
                providerCode: key,
                consentValue,
                shouldShow,
            });
        }
    });
    return clinicsConsents;
};

/**
 * Initializes the state of clinic checkboxes
 * @param {Array} clinicsConsents - array from getClinicsConsents
 * @returns {Object} object of the form {providerCode: boolean}
 */
export const initializeClinicsConsentsState = (clinicsConsents) => {
    return clinicsConsents.reduce((acc, clinic) => {
        acc[clinic.providerCode] = clinic.consentValue || false;
        return acc;
    }, {});
};


function isConsentValueValid(value) {
    return value === true || value === false;
}

function hasInvalidMainConsents(main, keys) {
    return keys.some(key => !isConsentValueValid(main[key]));
}

function hasInvalidProviderConsents(obj, providerKey, offline) {
    // gather provider codes (exclude 'main' and any providers in offline list)
    const providerCodes = Object.keys(obj)
        .filter(k => k !== 'main')
        .filter(k => !offline?.includes(k));

    for (const code of providerCodes) {
        const consent = obj[code]?.[providerKey];
        if (!isConsentValueValid(consent)) {
            return true;
        }
    }
    return false;
}

/**
 * Checks for profiles without consents according to business logic
 * @param {Object} consents - consents object
 * @returns {boolean} true if there is at least one profile/consent not true/false
 */
export function hasProfilesWithoutConsents(consents) {
    const state = store?.getState?.() ?? {};
    const preparedConsents = filterConsentsByCards(consents, state?.user?.info ?? {});
    const offline = store?.getState?.()?.consents?.providerErrors ?? [];
    // Check user
    const user = preparedConsents?.user;
    if (user) {
        const main = user.main || {};
        if (hasInvalidMainConsents(main, [ USER_AGREEMENT_KEY, USER_HISTORY_KEY ])) {
            return true;
        }
        if (hasInvalidProviderConsents(user, USER_CLINIC_HISTORY_KEY, offline)) {
            return true;
        }
    }

    // Check family
    const family = preparedConsents?.family;
    if (family && typeof family === 'object') {
        for (const profileId of Object.keys(family)) {
            const profile = family[profileId];
            if (!profile) {
                continue;
            }
            const main = profile.main || {};
            if (hasInvalidMainConsents(main, [ CHILD_HISTORY_KEY, STORE_PROFILE_CHILD ])) {
                return true;
            }
            if (hasInvalidProviderConsents(profile, CHILD_CLINIC_HISTORY_KEY, offline)) {
                return true;
            }
        }
    }
    return false;
}


export const userHaveNeededConsents = (user = {}, consents = []) => {
    let keys = Object.keys(user);
    if (consents?.every((c = '') => c.includes('CLINIC'))) {
        keys = keys?.filter?.(k => k !== 'main');
    } else {
        keys = [ 'main' ];
    }

    return keys.every((scope) => {
        return consents.every((consentKey) => {
            const value = user[scope]?.[consentKey];
            return value !== null && value !== undefined;
        });
    });
};

export const haveNeededConsent = (consents) => {
    const userMain = userHaveNeededConsents(consents?.user, [ USER_AGREEMENT_KEY, USER_HISTORY_KEY ]);
    const userClinics = userHaveNeededConsents(consents?.user, [ USER_CLINIC_HISTORY_KEY ]);
    const family = Object.values(consents?.family ?? {});
    const familyHaveMain = family?.every?.(child => userHaveNeededConsents(child, [ CHILD_HISTORY_KEY, STORE_PROFILE_CHILD ]));
    const familyHaveClinic = family?.every?.(child => userHaveNeededConsents(child, [ CHILD_CLINIC_HISTORY_KEY ]));
    return userMain && userClinics && familyHaveMain && familyHaveClinic;
};

export const onCheckUserConsents = ({
    currentConsent = {},
    from = null,
    to,
    codePosition
} = {}) => {
    const state = store.getState?.() ?? {};
    if (state?.user?.isAuthenticated === false || state?.user?.info?.me?.name === '') {
        return;
    }
    const consents = makeConsentObject(state?.consents, state?.providerCodes, true);
    const filteredConsents = filterConsentsByCards(consents, state?.user?.info ?? {});
    const have = haveNeededConsent(filteredConsents);

    console.log('[onCheckUserConsents] Consents to check ConsentConfirmation screen', {consents, filteredConsents, have, codePosition, providers: state?.providerCodes, to, import: Object.ImportVisitData, src: state?.consents});

    const showProgramPopup = () => {
        Object.ConsentConfirmed = true;
        console.log('Check show program popup', {1: Object.isAddProgram, 2: Object.newProgram});
        if (Object.isAddProgram && Object.newProgram) {
            setTimeout(() => {
                eventBus.emit('onNewUserItemsFound', {newProgram: Object.newProgram, onRequestClose: Object.onRequestCloseNewProgram, onClose: Object.onCloseNewProgram});
            }, 1000);
        } else {
            doCheckImportVisit('showProgramPopup');
        }
    }

    if (!have) {
        setTimeout(() => {
            navigate('ConsentConfirmation', {from, currentConsent, to});
        }, 0);
    } else if (to) {
        showProgramPopup();
        navigate(to);
    } else {
        showProgramPopup();
    }
};

export const consentsToArray = (consentsObj) => {
    if (!consentsObj || typeof consentsObj !== 'object') return [];
    return Object.entries(consentsObj).map(([type, provided]) => ({ type, provided }));
}

export const isProviderHasCapability = ({capabilities, providerCode}) => {
    if (!capabilities || !providerCode) {
        return false;
    }
    const providerCaps = capabilities[providerCode];
    if (typeof providerCaps === 'undefined') {
        return false;
    } else if (!Array.isArray(providerCaps) || providerCaps.length === 0) {
        return false;
    }
    return providerCaps.includes('MedicalHistoryConsentGathering') || providerCaps.includes('OnlineBookingConsentGathering');
}
